import { memo, ReactNode } from "react";
import { CellContext } from "@tanstack/react-table";
import classNames from "classnames";
import { getPredefinedGroupLabel } from "@common/constants/domains/accounting/accountGroups";
import { ChartOfAccountsRow } from "@components/accounting/chartOfAccounts/utils";
import coaStyles from "@components/accounting/chartOfAccounts/styles.module.scss";
import { Tag } from "@design-system/Tag/Tag";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { CoaMappingPreviewTableOptions } from "../coaMappingPreviewTableMeta";
import styles from "../CoaMappingPreviewStep.module.scss";

const NEW_ACCOUNT_TOOLTIP =
    "Created as a new Kick account when you confirm the mapping";

const NameContent = ({ children }: { children: ReactNode }) => (
    <ViewTableCell>
        <div
            className={classNames(
                coaStyles.columnContent,
                coaStyles.nameColumn,
            )}
        >
            {children}
        </div>
    </ViewTableCell>
);

const ChildCount = ({ count }: { count: number | undefined }) =>
    count ? <span className={coaStyles.counter}>{count}</span> : null;

export const CoaMappingPreviewNameCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ChartOfAccountsRow, unknown>) => {
        const { options } = useViewTableMeta<
            Record<string, never>,
            CoaMappingPreviewTableOptions
        >(table);
        const rowData = row.original;
        const childCount = rowData.accounts?.length;

        const predefinedGroup = rowData.section ?? rowData.predefinedType;
        if (predefinedGroup) {
            return (
                <NameContent>
                    {getPredefinedGroupLabel(predefinedGroup)}
                    <ChildCount count={childCount} />
                </NameContent>
            );
        }
        if (rowData.classification) {
            return (
                <NameContent>
                    {rowData.classification}
                    <ChildCount count={childCount} />
                </NameContent>
            );
        }
        if (rowData.group) {
            return (
                <NameContent>
                    {rowData.group.name}
                    <ChildCount count={childCount} />
                </NameContent>
            );
        }
        if (!rowData.account) {
            return <ViewTableCell />;
        }

        const { account } = rowData;
        const isNew = options?.newAccountIds.has(account.id) ?? false;
        const hasCodePrefix =
            (options?.showAccountNumbers ?? false) && account.codeText !== null;
        return (
            <NameContent>
                <span className={styles.accountName}>
                    <span>
                        {hasCodePrefix && `${account.codeText} - `}
                        {account.name}
                    </span>
                    {isNew && (
                        <Tooltip content={NEW_ACCOUNT_TOOLTIP}>
                            <span>
                                <Tag color="accent" size="sm">
                                    New
                                </Tag>
                            </span>
                        </Tooltip>
                    )}
                    {account.isDisabled && (
                        <Tag color="neutral" size="sm">
                            Archived
                        </Tag>
                    )}
                </span>
            </NameContent>
        );
    },
);

CoaMappingPreviewNameCell.displayName = "CoaMappingPreviewNameCell";
